import axios from "axios";
import { urlBase, urlBaseTemplates } from "./config";
import axiosInstance from "../services/config/axiosinstance";

// Endpoint público — verifica se o participante já respondeu esse formulário
// nesse evento. Aceita `participantId` OU `email` como identificador.
// Retorna { answered: boolean } no body.
async function checkFormResponsePublic({
  eventId,
  formId,
  participantId,
  email,
}) {
  try {
    const query = participantId
      ? `participantId=${encodeURIComponent(participantId)}`
      : `email=${encodeURIComponent(email)}`;
    const url = `${urlBase}/events/${eventId}/forms/${formId}/form-responses/check?${query}`;
    const response = await axios.get(url, {
      headers: { "Content-Type": "application/json" },
    });
    return response;
  } catch (error) {
    return error.response;
  }
}

// Endpoint público — busca dados do participante (nome/email) por id OU email.
// Se `eventId` for informado, o backend também valida se o participante
// está associado ao evento (retorna 404 se não estiver).
//
// Rotas suportadas pelo backend:
//   GET /attendees/:id
//   GET /attendees/:id?eventId=...
//   GET /attendees?email=...
//   GET /attendees?email=...&eventId=...
//
// Usado na rota /template/:id (form não-cadastro) — pré-preenche Nome/Email
// como readOnly quando paxid/email na URL identificam um participante válido.
async function getAttendeeByIdPublic({ id, email, eventId }) {
  try {
    const params = new URLSearchParams();
    if (!id && email) params.append("email", email);
    if (eventId) params.append("eventId", eventId);
    const qs = params.toString();
    const path = id ? `/attendees/${id}` : "/attendees";
    const url = `${urlBase}${path}${qs ? `?${qs}` : ""}`;
    const response = await axios.get(url, {
      headers: { "Content-Type": "application/json" },
    });
    return response;
  } catch (error) {
    return error.response;
  }
}

// Endpoint público — usa `axios` cru (não injeta JWT), pra ser consumido pela
// rota /template/:id sem autenticação. Backend precisa expor sem token.
async function getFormByIdPublic({ formId }) {
  try {
    const response = await axios.get(`${urlBase}/forms/${formId}`, {
      headers: { "Content-Type": "application/json" },
    });
    return response;
  } catch (error) {
    return error.response;
  }
}

// Envia respostas do form pela rota pública (/template/:id).
// Payload varia por typeForm:
//   - cadastro:  { typeForm, eventId, formId, formSnapshot, formColumns[] }
//   - outros:    { typeForm, eventId, formId, participantId, formColumns[] }
// Retorna a response axios (inclui status pra distinguir 201 / 409 / etc.).
async function submitFormResponsePublic({ payload }) {
  try {
    const response = await axios.post(`${urlBase}/form-responses`, payload, {
      headers: { "Content-Type": "application/json" },
    });
    return response;
  } catch (error) {
    return error.response;
  }
}

async function getAllForms({ jwt, offset, limit, search, eventId }) {
  try {
    const config = {
      method: "get",
      url: `${urlBase}/events/${eventId}/forms?offset=${offset}&limit=${limit}${
        search ? `&search=${search}` : ""
      }`,
      headers: {
        "Content-Type": "application/json",
      },
    };
    const response = await axiosInstance(config);
    return response;
  } catch (error) {
    return error.response;
  }
}

async function getUserResponse({ formId, participantId }) {
  try {
    const config = {
      method: "get",
      url: `${urlBase}/form-responses/forms/${formId}/participants/${participantId}`,
      headers: {
        "Content-Type": "application/json",
      },
    };
    const response = await axiosInstance(config);
    return response.data;
  } catch (error) {
    return error.response;
  }
}

async function createUserResponse({ formId, participantId, data }) {
  try {
    const config = {
      method: "put",
      url: `${urlBase}/form-responses/forms/${formId}/participants/${participantId}`,
      headers: {
        "Content-Type": "application/json",
      },
      data,
    };
    const response = await axiosInstance(config);
    return response.data;
  } catch (error) {
    return error.response;
  }
}

async function createForm({ jwt, data }) {
  try {
    const config = {
      method: "post",
      url: `${urlBase}/forms`,
      headers: {
        "Content-Type": "application/json",
      },
      data,
    };

    const response = await axiosInstance(config);
    return response;
  } catch (error) {
    return error.response;
  }
}

async function updateForm({ jwt, data }) {
  try {
    const config = {
      method: "put",
      url: `${urlBase}/forms`,
      headers: {
        "Content-Type": "application/json",
      },
      data,
    };

    const response = await axiosInstance(config);
    return response;
  } catch (error) {
    return error.response;
  }
}

async function deleteForm({ jwt, id }) {
  try {
    const config = {
      method: "delete",
      url: `${urlBase}/forms/${id}`,
      headers: {
        "Content-Type": "application/json",
      },
    };

    const response = await axiosInstance(config);
    return response;
  } catch (error) {
    return error.response;
  }
}

//TEMPLATE SYSTEM
//https://gateway-admin-plus.inteegrav2.com.br/api-admin-template/templates

async function createTemplate({ jwt, payload }) {
  try {
    const config = {
      method: "post",
      url: `${urlBaseTemplates}/templates`,
      headers: {
        "Content-Type": "application/json",
      },
      data: payload,
    };
    const response = await axiosInstance(config);
    return response;
  } catch (error) {
    return error;
  }
}

async function updateTemplate({ jwt, payload }) {
  try {
    const config = {
      method: "put",
      url: `${urlBaseTemplates}/templates`,
      headers: {
        "Content-Type": "application/json",
      },
      data: payload,
    };
    const response = await axiosInstance(config);
    return response;
  } catch (error) {
    return error;
  }
}

async function deleteTemplate({ jwt, payload }) {
  try {
    const config = {
      method: "DELETE",
      url: `${urlBaseTemplates}/templates/${payload.id}`,
      headers: {
        "Content-Type": "application/json",
      },
    };
    const response = await axiosInstance(config);
    return response;
  } catch (error) {
    return error;
  }
}

async function listTemplates({
  id,
  jwt,
  offset,
  limit,
  type,
  name,
  isSystemTemplate,
}) {
  try {
    let url = id
      ? `${urlBaseTemplates}/templates?eventId=${id}&offset=${offset}&limit=${
          limit || 10
        }&type=${type}&name=${name}`
      : `${urlBaseTemplates}/templates/defaults?offset=${offset}&limit=${
          limit || 10
        }&type=${type}&name=${name}`;
    if (typeof isSystemTemplate === "boolean") {
      url += `&isSystemTemplate=${isSystemTemplate}`;
    }
    const config = {
      method: "get",
      url: url,
      headers: {
        "Content-Type": "application/json",
      },
    };
    const response = await axiosInstance(config);
    return response;
  } catch (error) {
    return error;
  }
}

export {
  getAllForms,
  getFormByIdPublic,
  getAttendeeByIdPublic,
  checkFormResponsePublic,
  submitFormResponsePublic,
  createForm,
  updateForm,
  deleteForm,
  createTemplate,
  updateTemplate,
  listTemplates,
  deleteTemplate,
  getUserResponse,
  createUserResponse,
};
